export type PaymentMethod =
  | "online"
  | "check"
  | "cash"
  | "venmo"
  | "wire"
  | "invoice"
  | "other";

export interface CartConstraint {
  offering_id?: string;
  template_id: string;
  template_label: string;
  fee: number;
  requires_review?: boolean;
  severity?: string | null;
  constraint_type?: string | null;
  required_fields?: string[];
  input_field_specs?: Array<{
    field_key: string;
    label: string;
    input_type: "date" | "time" | "team_select" | "team_multiselect";
    required?: boolean;
    options_source?: string;
    help_text?: string;
    validation?: string;
  }>;
  params: Record<string, unknown>;
  notes?: string;
}

export interface CartExtra {
  extra_id: string;
  name: string;
  qty: number;
  price: number;
  // Attribution — the cart item (and resolved team) this extra was bought for.
  // Extras are always purchased inside a single team's block.
  team_client_id?: string;
  team_id?: string | null;
  // Conditional inputs driven by event_extras flags
  requires_team?: boolean;
  requires_player?: boolean;
  requires_game_date?: boolean;

  team_name?: string | null;
  division?: string | null;
  player_name?: string | null;
  player_jersey_number?: string | null;
  // Optional social handles — video/photo products tag the club when content
  // goes out, so we collect them at purchase rather than chasing them later.
  player_instagram_handle?: string | null;
  player_x_handle?: string | null;
  game_date?: string | null;
}


export interface CartItem {
  client_id: string; // local id for editing
  event_id: string;
  event_name: string;
  event_start_date?: string | null;

  team_name: string;
  team_id: string | null;
  /** RecruitLook owns teams; this is the reference stored on the registration. */
  recruitlook_team_id?: string | null;
  // Club-first registration (Slice D27)
  club_id?: string | null;
  club_name?: string | null;
  new_club_name?: string | null;
  new_short_name?: string | null;
  team_display_name?: string;
  // Raw string the coach typed — stored as a team alias so the next coach
  // who types it matches instantly.
  raw_team_input?: string | null;
  // 5-digit home ZIP — used to tell if the team is local enough to be exempt
  // from stay-to-play hotel requirements.
  team_home_zip?: string | null;
  // Plain lowercase age group, e.g. "14u" (never "Boys 14u").
  age_group?: string | null;
  catalog_division_id: string;
  division_label: string;
  gender: "boys" | "girls";
  base_price: number;
  coach_name: string;
  coach_email: string;
  coach_phone?: string;
  registrant_is_coach: boolean;
  /** @deprecated payment is a cart-level choice — see Cart.payment_method_chosen */
  payment_method_chosen?: never;
  /** @deprecated payment is a cart-level choice — see Cart.payment_promise_notes */
  payment_promise_notes?: never;

  requested_constraints: CartConstraint[];
  extras: CartExtra[];
  expected_roster_size?: number;
  // Team matching metadata (Slice D26/D27)
  // The organiser's tracked row this team came from, when the coach picked it
  // out of the match list. Links the registration back on submit.
  tracking_id?: string | null;
  matched_via?: "fuzzy_match" | "rejected_matches" | "no_matches_found" | "existing_squad" | "new_squad" | "new_club" | null;
  match_score?: number | null;
  rejected_match_ids?: string[];
  pool?: string | null;
  // D30 — scheduling tier
  scheduling_tier?: "standard" | "priority" | "club_priority" | "showcase";
  scheduling_tier_fee?: number;
  // Whether the chosen tier includes scheduling requests for free (waives
  // per-request constraint fees). Set from event config when the tier is chosen.
  scheduling_tier_waives_fees?: boolean;
  // D39 — in-progress scheduling request draft (persists across step nav)
  scheduling_draft?: {
    offering_id: string;
    template_id: string;
    template_label: string;
    fee: number;
    requires_review: boolean;
    severity: string | null;
    constraint_type: string | null;
    input_field_specs: any[];
    params: Record<string, any>;
  } | null;
}

export interface Cart {
  id: string | null;
  items: CartItem[];
  coupon_code?: string;
  coupon_applied?: {
    code: string;
    description?: string | null;
    discount_type: string;
    discount_value: number;
    discount_amount: number;
    /** "order_total" (default) or "entry_fees" — what the coupon discounts. */
    applies_to?: string;
    /** "per_order" (default) or "per_team" — only for fixed amounts. */
    fixed_scope?: string;
    /** When true, automatic discount policies are suppressed. */
    overrides_policies?: boolean;
    /** Per-team allocation keyed by cart item client_id. */
    per_team?: Record<string, number>;
  } | null;

  registrant_user_id: string;
  registrant_email: string;
  registrant_name: string;
  registrant_first_name?: string;
  registrant_last_name?: string;
  registrant_phone?: string;
  // Payment is ONE choice for the whole checkout — never per team.
  payment_method_chosen?: PaymentMethod;
  payment_promise_notes?: string;
}


export const CART_STORAGE_KEY = "rle_registration_cart_v1";

export function tierWaivesFees(item: CartItem): boolean {
  const tier = item.scheduling_tier ?? "standard";
  if (tier === "standard") return false;
  // Per-item flag (set from event config when the tier is chosen) is
  // authoritative; fall back to "paid tiers waive" for older cart items.
  if (typeof item.scheduling_tier_waives_fees === "boolean") {
    return item.scheduling_tier_waives_fees;
  }
  return tier === "priority" || tier === "club_priority" || tier === "showcase";
}

export function itemTotal(item: CartItem): number {
  const constraints = tierWaivesFees(item)
    ? 0
    : item.requested_constraints.reduce((s, c) => s + (c.fee || 0), 0);
  const extras = item.extras.reduce((s, e) => s + e.price * e.qty, 0);
  const tierFee = Number(item.scheduling_tier_fee || 0);
  return (item.base_price || 0) + constraints + extras + tierFee;
}
